import React from 'react';
import { Box, FormControl } from '@material-ui/core';
import { BILLING_METHODS, FormBodyAPI } from '../../../constants';
import { BaseTextField } from '../../TextField';
import SettingsToggleCard from '../SettingsToggleCard';
import { FormLabel } from '../../Forms/FormLabel';
import BaseTypography from '../../Text/BaseTypography';

import { red } from '../../../theme/colors';
import { validationScheme } from './useBankingSettingsFormValidation';

const billingDefaultsOptions = [
  {
    fieldName: 'allowPaymentViaCC',
    title: 'Allow payment via credit card',
    description: `Turn this on if you want your clients to 
                    be able to select credit cards as their payment method. `,
  },
  {
    fieldName: 'allowPaymentViaACH',
    title: 'Allow payment via ACH',
    description: `Turn this on if you want your clients to be able to select ACH as their payment method. ACH is only supported for USD. For other currencies this preference is ignored.`,
  },
  {
    fieldName: 'absorbTransactionFees',
    title: 'Absorb transaction fees',
    description: `Turn this on if you want to absorb payment processing fees. `,
  },
];

interface BillingDefaultFormProps {
  isStripeEntityForm: boolean;
  isInvoiceEntity: boolean;
}

export const useBankingDefaultsSettingsForm = (currency?: string) => {
  const getSubmitValues = async () => ({});

  const FormRenderer: React.FC<FormBodyAPI & BillingDefaultFormProps> = ({
    errors,
    values,
    handleBlur,
    handleChange,
    isStripeEntityForm = false,
    isInvoiceEntity = false,
  }) => {
    const errorFields = isStripeEntityForm ? errors.fields : errors;

    const formValues = isStripeEntityForm ? values.fields : values;
    const collectionMethod = isStripeEntityForm
      ? formValues.collectionMethod
      : '';

    // Since we're showing billing settings form as part
    // of stripe entity edit form, we need to provide
    // the root object name to get toggle card values from.
    const rootField = isStripeEntityForm ? 'fields.' : '';

    // for auto-charge invoice, don't show 'Allow payment via *' options
    const filteredBillingDefaultsOptions = React.useMemo(
      () =>
        billingDefaultsOptions.filter(item => {
          // dont show ACH payment setting for non-usd currencies
          const isACHPaymentMethod = item.fieldName === 'allowPaymentViaACH';
          if (
            isACHPaymentMethod &&
            currency &&
            currency.toUpperCase() !== 'USD'
          ) {
            // setFieldValue('fields.allowPaymentViaACH', false);
            return false;
          }

          // dont show payment method settings when auto charging an invoice
          const isPaymentMethodSetting =
            item.fieldName.indexOf('allowPaymentVia') > -1;
          if (
            isPaymentMethodSetting &&
            collectionMethod === BILLING_METHODS.CHARGE_AUTOMATICALLY &&
            isInvoiceEntity
          ) {
            return false;
          }
          return true;
        }),
      [collectionMethod, currency, isInvoiceEntity],
    );

    return (
      <Box width={1} position="relative" pb={5}>
        {filteredBillingDefaultsOptions.map(option => (
          <SettingsToggleCard
            titleFontType={isStripeEntityForm ? '13Regular' : '13Medium'}
            key={Date.now() + option.fieldName}
            checked={!!formValues[option.fieldName]}
            fieldName={`${rootField}${option.fieldName}`}
            title={option.title}
            description={option.description}
          />
        ))}
        {errorFields?.allowPaymentViaCC && (
          <BaseTypography fontType="13Medium" style={{ color: red }}>
            {errorFields.allowPaymentViaCC}
          </BaseTypography>
        )}

        {// Do not show default memo text field in stripe entity form
        !isStripeEntityForm && (
          <Box my={4}>
            <FormControl fullWidth>
              <FormLabel
                primaryText="Default memo"
                secondaryText=" - This will prefill the memo field in the “create an invoice/subscription” flow"
              />
              <BaseTextField
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                name="defaultMemo"
                value={values.defaultMemo}
              />
            </FormControl>
          </Box>
        )}
      </Box>
    );
  };

  return {
    validationScheme,
    getSubmitValues,
    FormRenderer,
  };
};
